import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Firestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  addDoc,
} from '@angular/fire/firestore';
import { Subscription } from 'rxjs';
import { Auth } from '@angular/fire/auth';
import { RealtimeBookingService } from 'src/app/shared/services/realtime-booking.service';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { MatDialog } from '@angular/material/dialog';
import { LpoViewerDialogComponent } from 'src/app/components/lpo-viewer-dialog/lpo-viewer-dialog.component';
import { TimesheetEntryDialogComponent } from '../timesheet-entry-dialog/timesheet-entry-dialog.component';
import { InvoiceTemplateDialogComponent } from 'src/app/components/invoice-template-dialog/invoice-template-dialog.component';
import { ReviewDialogComponent } from '../../review-dialog/review-dialog.component';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { BookingLockService } from 'src/app/shared/services/booking-lock.service';
import { QuotationViewerDialogComponent } from 'src/app/components/quotation-viewer-dialog/quotation-viewer-dialog.component';

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss'],
})
export class OrderDetailsComponent implements OnInit, OnDestroy {
  bookingId!: string;
  bookingData: any;
  vendorDetails: any;
  isLoading = true;
  totalAmount: number = 0;
  duration: string = '-';
  reviewExists: boolean = false;
  reviewId: string | null = null;
  fullReview: any = null;
  vendorAvgRating: number = 0;
  vendorReviewCount: number = 0;
  private bookingSub?: Subscription;

  highlightLpo = false;
  highlightTimesheet = false;
  highlightInvoice = false;

  private isFirstSync = true;

  // Track last seen update times to detect new changes while viewing
  private lastLpoUpdate?: number;
  private lastTimesheetUpdate?: number;
  private lastInvoiceUpdate?: number;

  @ViewChild('cancelReasonDialog') cancelReasonDialog!: TemplateRef<any>;
  cancelReasonList: string[] = [
    'Change of plans / No longer needed',
    'Found a better alternative / Price',
    'Incorrect booking dates or details',
    'Project delayed or cancelled',
    'Other (Please specify)',
  ];
  selectedCancelReason: string = '';
  cancelMessage: string = '';

  constructor(
    private afs: Firestore,
    private route: ActivatedRoute,
    private router: Router,
    private rtBooking: RealtimeBookingService,
    private ux: UxserviceService,
    private dialog: MatDialog,
    private auth: Auth,
    private notificationService: NotificationService,
    private bookingLock: BookingLockService,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    this.bookingId = this.route.snapshot.paramMap.get('id') || '';
    if (this.bookingId) {
      this.ux.showOverlay('Loading booking details...');
      try {
        await this.loadBooking();
        await this.loadVendorDetails();
        await this.loadReviewUserInfo();

        // Mark any notifications for this booking as read
        if (this.bookingId) {
          this.notificationService.markBookingNotificationsAsRead(
            this.bookingId,
          );
        }

        this.initRealtimeSync();
      } finally {
        this.ux.instantCloseOverlay();
      }
    }
  }

  initRealtimeSync() {
    this.bookingSub = this.rtBooking
      .watchBooking(this.bookingId)
      .subscribe((rtData) => {
        if (rtData && this.bookingData) {
          // If first sync, just record current state timestamps but don't pulse
          if (this.isFirstSync) {
            this.lastLpoUpdate = rtData.lpo?.updatedAt;
            this.lastTimesheetUpdate = rtData.timesheet?.updatedAt;
            this.lastInvoiceUpdate = rtData.invoice?.updatedAt;
            this.isFirstSync = false;
          }
          // 1. Status Sync
          if (rtData.status && rtData.status !== this.bookingData.status) {
            this.bookingData.status = this.capitalize(rtData.status);
            if (rtData.reason) {
              this.bookingData.reason = rtData.reason;
            }
          }

          if (rtData.lpo) {
            // Check for new updates - ONLY if not updated by me
            if (
              !this.isFirstSync &&
              rtData.lpo.updatedBy !== this.auth.currentUser?.uid &&
              rtData.lpo.updatedAt > (this.lastLpoUpdate || 0)
            ) {
              this.highlightLpo = true;
            }
            this.lastLpoUpdate = rtData.lpo.updatedAt;

            if (!this.bookingData.lpo) this.bookingData.lpo = {};

            if (rtData.lpo.url) {
              this.bookingData.lpo.url = rtData.lpo.url;
              this.bookingData.LPO = rtData.lpo.url;
            }

            if (rtData.lpo.generated !== undefined) {
              this.bookingData.lpoGenerated = rtData.lpo.generated;
            }

            if (rtData.lpo.approved !== undefined) {
              this.bookingData.lpoApproved = rtData.lpo.approved;
            }

            if (rtData.lpo.rejected !== undefined) {
              this.bookingData.lpoRejected = rtData.lpo.rejected;
            }
            this.bookingData.lpoRejectionReason =
              rtData.lpo.rejectionReason || '';

            if (rtData.lpo.date) {
              this.bookingData.lpoDate = rtData.lpo.date;
            }

            if (rtData.lpo.deliveryCharge !== undefined) {
              this.bookingData.deliveryCharge = rtData.lpo.deliveryCharge;
            }
            if (rtData.lpo.discount !== undefined) {
              this.bookingData.discount = rtData.lpo.discount;
            }
            if (rtData.lpo.awaitingCustomerPriceConsent !== undefined) {
              this.bookingData.awaitingCustomerPriceConsent =
                rtData.lpo.awaitingCustomerPriceConsent;
            }
            if (rtData.lpo.totalAmount !== undefined) {
              this.totalAmount = rtData.lpo.totalAmount;
              this.bookingData.totalAmount = rtData.lpo.totalAmount;
            }
            if (rtData.lpo.approvedByVendor !== undefined) {
              this.bookingData.lpoApprovedByVendor =
                rtData.lpo.approvedByVendor;
            }

            // Sync signatures
            if (rtData.lpo.customerSignature) {
              this.bookingData.lpo.customerSignature =
                rtData.lpo.customerSignature;
              this.bookingData.customerSignatureUrl =
                rtData.lpo.customerSignature;
            }
            if (rtData.lpo.customerSignatureBase64) {
              this.bookingData.lpo.customerSignatureBase64 =
                rtData.lpo.customerSignatureBase64;
              this.bookingData.customerSignatureBase64 =
                rtData.lpo.customerSignatureBase64;
            }
          }

          // 3. Timesheet & Invoice Sync
          if (rtData.timesheet) {
            // Check for new updates - ONLY if not updated by me
            if (
              !this.isFirstSync &&
              rtData.timesheet.updatedBy !== this.auth.currentUser?.uid &&
              rtData.timesheet.updatedAt > (this.lastTimesheetUpdate || 0)
            ) {
              this.highlightTimesheet = true;
            }
            this.lastTimesheetUpdate = rtData.timesheet.updatedAt;
            this.bookingData.timesheet = rtData.timesheet;

            // Highlight timesheet option if it is not finalized yet
            if (rtData.timesheet.isCompleted) {
              this.highlightTimesheet = false;
            } else {
              this.highlightTimesheet = true;
            }
          }
          if (rtData.invoice) {
            // Check for new updates - ONLY if not updated by me
            if (
              !this.isFirstSync &&
              rtData.invoice.updatedBy !== this.auth.currentUser?.uid &&
              rtData.invoice.updatedAt > (this.lastInvoiceUpdate || 0)
            ) {
              this.highlightInvoice = true;
            }
            this.lastInvoiceUpdate = rtData.invoice.updatedAt;

            this.bookingData.invoice = rtData.invoice;
            if (rtData.invoice.finalized !== undefined) {
              this.bookingData.invoiceFinalized = rtData.invoice.finalized;
            }
            if (rtData.invoice.url) {
              this.bookingData.invoiceUrl = rtData.invoice.url;
            }
            if (rtData.invoice.vendorSig) {
              this.bookingData.vendorSignatureUrl = rtData.invoice.vendorSig;
            }
            if (rtData.invoice.vendorSigBase64) {
              this.bookingData.vendorSignatureBase64 =
                rtData.invoice.vendorSigBase64;
            }
          }
          if (rtData.quotation) {
            if (!this.bookingData.quotation) this.bookingData.quotation = {};
            if (rtData.quotation.generated !== undefined)
              this.bookingData.quotationGenerated = rtData.quotation.generated;
            if (rtData.quotation.approved !== undefined)
              this.bookingData.quotationApproved = rtData.quotation.approved;
            if (rtData.quotation.date)
              this.bookingData.quotationDate = rtData.quotation.date;
            if (rtData.quotation.deliveryCharge !== undefined)
              this.bookingData.deliveryCharge = rtData.quotation.deliveryCharge;
            if (rtData.quotation.discount !== undefined)
              this.bookingData.discount = rtData.quotation.discount;
          }
          if (rtData.vendorConfirmed !== undefined) {
            this.bookingData.vendorConfirmed = rtData.vendorConfirmed;
          }

          // Compute highlightLpo based on updated state
          const status = this.bookingData.status;
          const lpoGenerated = this.bookingData.lpoGenerated || (this.bookingData.lpo && this.bookingData.lpo.generated);
          const lpoUrl = this.bookingData.LPO || (this.bookingData.lpo && this.bookingData.lpo.url);

          if ((status === 'Accepted' || status === 'Confirmed') && !lpoGenerated && !lpoUrl) {
            this.highlightLpo = true;
          } else {
            this.highlightLpo = false;
          }

          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy() {
    this.bookingSub?.unsubscribe();
  }

  async loadBooking() {
    try {
      const bookingRef = doc(this.afs, `booking-list/${this.bookingId}`);
      const snap = await getDoc(bookingRef);

      if (snap.exists()) {
        const data: any = snap.data();
        const fetchTasks: Promise<void>[] = [];

        // Fetch Equipment if needed
        if ((!data.equipment || !data.equipment.images) && data.equipmentId) {
          fetchTasks.push(
            (async () => {
              const eqRef = doc(this.afs, 'equipment-list', data.equipmentId);
              const eqSnap = await getDoc(eqRef);
              if (eqSnap.exists()) {
                data.equipment = {
                  ...eqSnap.data(),
                  ...(data.equipment || {}),
                };
              }
            })(),
          );
        }

        // Fetch Timesheet status
        fetchTasks.push(
          (async () => {
            const tsRef = doc(
              this.afs,
              `booking-list/${this.bookingId}/timesheets/main`,
            );
            const tsSnap = await getDoc(tsRef);
            if (tsSnap.exists()) {
              data.timesheet = tsSnap.data();
            }
          })(),
        );

        await Promise.all(fetchTasks);

        // Polyfill customer object
        if (!data.customer) data.customer = {};
        if (data.startDate) data.customer.startDate = data.startDate;
        if (data.endDate) data.customer.endDate = data.endDate;
        if (data.location) data.customer.location = data.location;
        if (data.priceType) data.customer.priceType = data.priceType;
        if (data.rate) data.customer.price = data.rate;

        this.bookingData = data;
        const start = this.getBookingDate(data.customer?.startDate);
        const end = this.getBookingDate(data.customer?.endDate);

        this.totalAmount = data.totalAmount || 0;

        this.duration = this.getDuration(start, end);
        this.bookingData.status = this.capitalize(data.status || 'Pending');
        this.bookingData.vendorConfirmed = !!data.vendorConfirmed;

        // Set LPO highlight flag if LPO upload is needed
        const status = this.bookingData.status;
        const lpoGenerated = data.lpo?.generated || data.lpoGenerated;
        const lpoUrl = data.lpo?.url || data.LPO;

        if ((status === 'Accepted' || status === 'Confirmed') && !lpoGenerated && !lpoUrl) {
          this.highlightLpo = true;
        } else {
          this.highlightLpo = false;
        }

        // Set Timesheet highlight flag if customer needs to sign/review it
        if (data.timesheet && !data.timesheet.isCompleted) {
          this.highlightTimesheet = true;
        } else {
          this.highlightTimesheet = false;
        }
      }
    } catch (error) {
      console.error('Error loading booking:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadVendorDetails() {
    if (
      !this.bookingData ||
      (!this.bookingData.vendorId && !this.bookingData.equipment?.bywhom)
    )
      return;
    try {
      const vendorId =
        this.bookingData.vendorId || this.bookingData.equipment.bywhom;
      const docRef = doc(this.afs, `users-list/${vendorId}`);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const u = snap.data();
        this.vendorDetails = {
          name: u['displayName'] || u['firstname'] || 'Vendor',
          companyName:
            u['companyName'] ||
            u['accountdetails']?.companyname ||
            'Unknown Company',
          phone: u['phoneNumber'] || u['phone'] || '',
          email: u['email'] || '',
          address:
            u['companyAddress'] || u['companyDetails'] || u['address'] || '',
          logo: u['companyLogoUrl'] || u['photoURL'] || '',
          trn: u['trnNumber'] || u['businessLicenseNo'] || '',
        };
        await this.fetchVendorRating(vendorId);
      }
    } catch (error) {
      console.error('Error loading vendor details:', error);
    }
  }

  async fetchVendorRating(vendorId: string) {
    try {
      const revRef = collection(this.afs, `vendor-reviews/${vendorId}/items`);
      const snap = await getDocs(revRef);
      if (snap.empty) {
        this.vendorAvgRating = 0;
        this.vendorReviewCount = 0;
        return;
      }

      let total = 0;
      snap.forEach((doc) => {
        const data = doc.data();
        total += data['rating'] || 0;
      });

      this.vendorReviewCount = snap.size;
      this.vendorAvgRating = Number((total / snap.size).toFixed(1));
    } catch (err) {
      console.error('Error fetching vendor rating:', err);
    }
  }

  getBookingDate(field: any): Date | null {
    if (!field) return null;
    if (typeof field.toDate === 'function') return field.toDate();
    if (field instanceof Date) return field;
    return new Date(field);
  }

  getDuration(start: Date | null, end: Date | null): string {
    if (!start || !end) return '-';
    const diff = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 3600 * 24),
    );
    return diff > 0 ? `${diff} days` : '1 day';
  }

  capitalize(value: string): string {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  isIncluded(type: string): boolean {
    if (!this.bookingData || !this.bookingData.equipment) return false;

    const priceType =
      this.bookingData.priceType || this.bookingData.customer?.priceType;
    if (!priceType) return false;

    const typeFieldMap: { [key: string]: string } = {
      daily: 'dailyWith',
      weekly: 'weeklyWith',
      monthly: 'monthlyWith',
      per_trip: 'tripWith',
    };

    const prefix = typeFieldMap[priceType] || 'dailyWith';
    const fieldName = prefix + type;
    const value = this.bookingData.equipment[fieldName];

    if (value !== undefined && value !== null) {
      return value === 'yes';
    }

    // Fallback if field the dynamic field doesn't exist
    if (type === 'Driver') return !!this.bookingData.equipment.withDriver;
    if (type === 'Fuel') return !!this.bookingData.equipment.withFuel;
    if (type === 'Salik') return !!this.bookingData.equipment.withSalik;

    return false;
  }

  getTimeAgo(dateField: any): string {
    const date = this.getBookingDate(dateField);
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHr / 24);

    if (diffDays > 0)
      return `created ${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHr > 0) return `created ${diffHr}hr ago`;
    if (diffMin > 0) return `created ${diffMin}min ago`;
    return 'created just now';
  }

  getDaysUntil(dateField: any): string {
    const date = this.getBookingDate(dateField);
    if (!date) return '';
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    if (diffMs < 0) {
      if (
        this.bookingData.status === 'Confirmed' ||
        this.bookingData.status === 'Completed'
      ) {
        return '(Started)';
      }
      return '';
    }
    const diffDays = Math.ceil(diffMs / (1000 * 3600 * 24));
    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days from now`;
  }

  get numberOfTrips(): number {
    if (!this.bookingData || !this.bookingData.customer) return 0;
    if (this.bookingData.customer.priceType !== 'per_trip') return 0;
    let priceVal = this.bookingData.customer.price || 0;
    if (typeof priceVal === 'string') {
      priceVal = Number(priceVal.replace(/,/g, ''));
    }
    const price = +priceVal;
    if (!price) return 0;
    return Math.round(this.totalAmount / price);
  }

  getFinalTotal(): number {
    if (!this.bookingData) return this.totalAmount;
    const delivery =
      Number(this.bookingData.deliveryCharge) ||
      Number(this.bookingData.quotation?.deliveryCharge) ||
      0;
    return (Number(this.totalAmount) || 0) + Number(delivery);
  }

  openLpoViewer(showUploadDirectly = false) {
    if (this.dialog.getDialogById('lpo-viewer-dialog')) return;
    this.highlightLpo = false;
    this.dialog.open(LpoViewerDialogComponent, {
      id: 'lpo-viewer-dialog',
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        booking: { ...this.bookingData, id: this.bookingId },
        showUploadDirectly: showUploadDirectly,
        isVendor: false,
      },
      panelClass: 'lpo-dialog',
      disableClose: true,
    });
  }

  openTimesheetViewer() {
    this.highlightTimesheet = false;
    this.router.navigate(['/timesheet', this.bookingId]);
  }

  openInvoiceViewer() {
    this.highlightInvoice = false;
    this.dialog.open(InvoiceTemplateDialogComponent, {
      width: '1200px',
      maxWidth: '98vw',
      maxHeight: '95vh',
      data: {
        bookingId: this.bookingId,
        booking: { ...this.bookingData, id: this.bookingId },
      },
      panelClass: 'invoice-dialog-panel',
    });
  }

  viewQuotation() {
    this.dialog.open(QuotationViewerDialogComponent, {
      width: '90%',
      maxWidth: '1200px',
      height: '90%',
      data: {
        booking: { ...this.bookingData, id: this.bookingId },
        isVendor: false,
        mode: 'edit',
      },
      disableClose: true,
    });
  }

  async loadReviewUserInfo() {
    const equipId =
      this.bookingData.equipmentId || this.bookingData.equipment?.id;
    const vendorId =
      this.bookingData.vendorId || this.bookingData.equipment?.bywhom;
    const userId = this.auth.currentUser?.uid;
    if (!equipId || !userId || !vendorId) return;

    try {
      // 1. Fetch Equipment Review
      const equipRevRef = collection(this.afs, `reviews/${equipId}/items`);
      const eqQuery = query(
        equipRevRef,
        where('userId', '==', userId),
        where('bookingId', '==', this.bookingId),
      );
      const eqSnap = await getDocs(eqQuery);

      // 2. Fetch Vendor Review
      const vendorRevRef = collection(
        this.afs,
        `vendor-reviews/${vendorId}/items`,
      );
      const vQuery = query(
        vendorRevRef,
        where('userId', '==', userId),
        where('bookingId', '==', this.bookingId),
      );
      const vSnap = await getDocs(vQuery);

      if (!eqSnap.empty || !vSnap.empty) {
        this.reviewExists = true;
        this.reviewId = !eqSnap.empty ? eqSnap.docs[0].id : null;

        this.fullReview = {
          equipment: !eqSnap.empty ? eqSnap.docs[0].data() : null,
          vendor: !vSnap.empty ? vSnap.docs[0].data() : null,
        };
      }
    } catch (err) {
      console.error('Error loading existing reviews:', err);
    }
  }

  openReviewDialog() {
    const dialogRef = this.dialog.open(ReviewDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      data: {
        equipmentId:
          this.bookingData.equipmentId || this.bookingData.equipment?.id,
        existingReview: this.fullReview,
        isRejected: this.capitalize(this.bookingData.status) === 'Rejected',
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) return;

      const equipId =
        this.bookingData.equipmentId || this.bookingData.equipment?.id;
      const vendorId = this.bookingData.equipment?.bywhom;
      const userId = this.auth.currentUser?.uid;

      if (!equipId || !userId || !vendorId) return;

      try {
        await this.ux.showOverlay('Submitting review...');

        // 1. SAVE EQUIPMENT REVIEW (Only if not rejected)
        const isRejected =
          this.capitalize(this.bookingData.status) === 'Rejected';
        if (!isRejected) {
          const equipReviewData = {
            ...result.equipment,
            finalReview: result.equipment.finalReview || '', // Matches BookingDetailComponent
            comment: result.equipment.finalReview || '', // Legacy support
            review: result.equipment.finalReview || '', // Generic support
            userId,
            bookingId: this.bookingId,
            updatedAt: new Date(),
          };

          if (this.reviewId) {
            const equipRevDoc = doc(
              this.afs,
              `reviews/${equipId}/items/${this.reviewId}`,
            );
            const updateObj = { ...equipReviewData };
            if (!this.fullReview?.createdAt)
              (updateObj as any).createdAt = new Date();
            await updateDoc(equipRevDoc, updateObj);
          } else {
            const equipRevColl = collection(
              this.afs,
              `reviews/${equipId}/items`,
            );
            const newDoc = await addDoc(equipRevColl, {
              ...equipReviewData,
              createdAt: new Date(),
            });
            this.reviewId = newDoc.id;
            this.reviewExists = true;
          }
        }

        // 2. SAVE VENDOR REVIEW (Separate Collection)
        const vendorReviewData = {
          ...result.vendor,
          finalReview: result.vendor.finalReview || '',
          comment: result.vendor.finalReview || '',
          review: result.vendor.finalReview || '',
          userId,
          bookingId: this.bookingId,
          equipmentId: equipId,
          updatedAt: new Date(),
        };

        // Use an ID that matches the equipment review ID or find existing by bookingId
        const vendorRevColl = collection(
          this.afs,
          `vendor-reviews/${vendorId}/items`,
        );
        const vQuery = query(
          vendorRevColl,
          where('bookingId', '==', this.bookingId),
        );
        const vSnap = await getDocs(vQuery);

        if (!vSnap.empty) {
          await updateDoc(
            doc(
              this.afs,
              `vendor-reviews/${vendorId}/items/${vSnap.docs[0].id}`,
            ),
            vendorReviewData,
          );
        } else {
          await addDoc(vendorRevColl, {
            ...vendorReviewData,
            createdAt: new Date(),
          });
        }

        this.fullReview = {
          equipment: result.equipment,
          vendor: result.vendor,
        };

        this.ux.showSuccess('Review submitted successfully!');
      } catch (err) {
        console.error('Review submission error', err);
        this.ux.showError('Failed to submit review');
      } finally {
        this.ux.instantCloseOverlay();
      }
    });
  }

  async cancelBooking() {
    this.selectedCancelReason = '';
    this.cancelMessage = '';
    const dialogRef = this.dialog.open(this.cancelReasonDialog, {
      width: '450px',
      disableClose: true,
      panelClass: 'modern-dialog',
    });

    dialogRef.afterClosed().subscribe(async (confirmed) => {
      if (confirmed && this.selectedCancelReason) {
        let finalReason = this.selectedCancelReason;
        if (this.cancelMessage.trim()) {
          finalReason += ` - ${this.cancelMessage.trim()}`;
        }

        try {
          this.ux.showOverlay('Cancelling booking...');

          const bookingRef = doc(this.afs, `booking-list/${this.bookingId}`);
          await updateDoc(bookingRef, {
            status: 'Cancelled',
            reason: finalReason,
            cancelledBy: 'Customer',
            cancelledAt: new Date(),
          });

          // Update RTDB for real-time synchronization
          await this.rtBooking.updateStatus(this.bookingId, 'Cancelled', {
            reason: finalReason,
            cancelledBy: 'Customer',
            cancelledAt: new Date().getTime(),
          });

          // Notify Vendor
          const vendorId =
            this.bookingData.vendorId || this.bookingData.equipment?.bywhom;
          if (vendorId) {
            this.notificationService.sendNotification(vendorId, {
              title: 'Booking Cancelled by Customer',
              message: `Booking #${this.bookingData.bookingId || this.bookingId} has been cancelled by the customer. Reason: ${finalReason}`,
              type: 'warning',
              link: '/business/booking',
              bookingId: this.bookingId,
            });
          }

          // Unlock vehicle dates
          const start = this.getBookingDate(
            this.bookingData.customer?.startDate,
          );
          const end = this.getBookingDate(this.bookingData.customer?.endDate);
          const equipmentId =
            this.bookingData.equipmentId || this.bookingData.equipment?.id;
          const customerId = this.auth.currentUser?.uid;

          if (start && end && equipmentId && customerId) {
            await this.bookingLock.unlockVehicle(
              equipmentId,
              start,
              end,
              customerId,
            );
          }

          this.ux.showSuccess('Booking cancelled successfully.');
          // UI will update via realtime sync
        } catch (error) {
          console.error('Error cancelling booking:', error);
          this.ux.showError('Failed to cancel booking.');
        } finally {
          this.ux.instantCloseOverlay();
        }
      }
    });
  }

  async acceptPriceAdjustment() {
    if (!this.bookingId || !this.bookingData) return;

    this.ux.showOverlay('Accepting price adjustments...');
    try {
      const bookingRef = doc(this.afs, `booking-list/${this.bookingId}`);
      await updateDoc(bookingRef, {
        awaitingCustomerPriceConsent: false,
        lpoApproved: true,
        status: 'Confirmed',
      });

      // Update RTDB
      await this.rtBooking.updateLpo(this.bookingId, {
        awaitingCustomerPriceConsent: false,
        approved: true,
      });
      await this.rtBooking.updateStatus(this.bookingId, 'Confirmed');

      // Notify Vendor
      const vendorId =
        this.bookingData.vendorId || this.bookingData.equipment?.bywhom;
      if (vendorId) {
        this.notificationService.sendNotification(vendorId, {
          title: 'Price Adjustments Accepted',
          message: `The customer has accepted the price adjustments for Booking #${this.bookingData.bookingId || this.bookingId}. The booking is now Confirmed.`,
          type: 'success',
          link: '/business/booking',
          bookingId: this.bookingId,
        });
      }

      this.ux.showSuccess(
        'Price adjustments accepted. Booking is now confirmed.',
      );
    } catch (e) {
      console.error('Error accepting price adjustment', e);
      this.ux.showError('Failed to accept adjustments.');
    } finally {
      this.ux.instantCloseOverlay();
    }
  }

  async rejectPriceAdjustment() {
    if (!this.bookingId || !this.bookingData) return;

    const confirmed = await this.ux.showConfirmation(
      'Are you sure you want to reject the price adjustments? The vendor will be notified to revise the charges.',
      'Reject Adjustments',
    );
    if (!confirmed) return;

    this.ux.showOverlay('Rejecting price adjustments...');
    try {
      const bookingRef = doc(this.afs, `booking-list/${this.bookingId}`);

      const deliveryCharge = Number(this.bookingData.deliveryCharge) || 0;
      const discount = Number(this.bookingData.discount) || 0;
      const adjustedTotal =
        Number(this.bookingData.totalAmount) || this.totalAmount;
      const originalTotal = adjustedTotal - deliveryCharge;

      // When rejecting, we reset the adjustments so the vendor can try again
      await updateDoc(bookingRef, {
        awaitingCustomerPriceConsent: false,
        lpoApprovedByVendor: false,
        priceAdjustmentRejected: true,
        deliveryCharge: 0,
        totalAmount: originalTotal,
      });

      // Update RTDB
      await this.rtBooking.updateLpo(this.bookingId, {
        awaitingCustomerPriceConsent: false,
        approvedByVendor: false,
        priceAdjustmentRejected: true,
        deliveryCharge: 0,
        totalAmount: originalTotal,
      });

      // Notify Vendor
      const vendorId =
        this.bookingData.vendorId || this.bookingData.equipment?.bywhom;
      if (vendorId) {
        this.notificationService.sendNotification(vendorId, {
          title: 'Price Adjustments Rejected',
          message: `The customer has rejected the price adjustments for Booking #${this.bookingData.bookingId || this.bookingId}. Please review and resubmit the LPO approval.`,
          type: 'warning',
          link: '/business/booking',
          bookingId: this.bookingId,
        });
      }

      this.ux.showSuccess(
        'Price adjustments rejected. The vendor has been notified.',
      );
    } catch (e) {
      console.error('Error rejecting price adjustment', e);
      this.ux.showError('Failed to reject adjustments.');
    } finally {
      this.ux.instantCloseOverlay();
    }
  }

  async cancelBookingDueToPrice() {
    if (!this.bookingId || !this.bookingData) return;

    const confirmed = await this.ux.showConfirmation(
      'Are you sure you want to Reject and Cancel this booking? This action will permanently cancel the request.',
      'Cancel Booking',
    );
    if (!confirmed) return;

    this.ux.showOverlay('Cancelling booking...');
    try {
      const bookingRef = doc(this.afs, `booking-list/${this.bookingId}`);

      // Update booking status to Cancelled
      await updateDoc(bookingRef, {
        status: 'Cancelled',
        reason: 'Customer rejected proposed price adjustments.',
        cancelledBy: 'Customer',
        cancelledAt: new Date(),
        awaitingCustomerPriceConsent: false,
      });

      // Update Realtime DB
      await this.rtBooking.updateStatus(this.bookingId, 'Cancelled', {
        reason: 'Customer rejected proposed price adjustments.',
        cancelledBy: 'Customer',
        cancelledAt: new Date().getTime(),
      });

      // Also clear LPO awaiting state in RTDB
      await this.rtBooking.updateLpo(this.bookingId, {
        awaitingCustomerPriceConsent: false,
        approvedByVendor: false,
      });

      // Notify Vendor
      const vendorId =
        this.bookingData.vendorId || this.bookingData.equipment?.bywhom;
      if (vendorId) {
        this.notificationService.sendNotification(vendorId, {
          title: 'Booking Cancelled (Price Rejected)',
          message: `Booking #${this.bookingData.bookingId || this.bookingId} has been cancelled by the customer due to rejected price adjustments.`,
          type: 'warning',
          link: '/business/booking',
          bookingId: this.bookingId,
        });
      }

      this.ux.showSuccess('Booking Cancelled.');
    } catch (e) {
      console.error('Error cancelling booking', e);
      this.ux.showError('Failed to cancel booking.');
    } finally {
      this.ux.instantCloseOverlay();
    }
  }

  goBack() {
    this.router.navigate(['/booking-orders']);
  }
}
